import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { radioStations, type RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { hostingTypeLabels } from '@/types/streaming';
import {
  getOccupiedFrequencies,
  getPlatformRadioStatus,
  TUNER_MIN_FREQ,
  TUNER_MAX_FREQ,
  TUNER_STEP,
} from '@/services/discovery';
import { fetchCentovaMetadata } from '@/services/centovaMetadata';
import { buildStreamUrl } from '@/services/streamUrlBuilder';
import type { RadioSearchResult, FrequencySlot } from '@/types/discovery';

/* ── Bandera de demo (desarrollo explícito, default false) ── */
const DEMO_ENABLED = import.meta.env.VITE_ENABLE_DEMO_DATA === 'true';

/* ── Parámetros del stream oficial de RadioFrecuenciaWeb ── */
const RFW_STREAM_PARAMS = {
  loginHostname: 'cast3.asurahosting.com',
  streamUsername: 'radiofrecuenciaweb',
  mountpoint: 'stream',
  streamingType: 'shoutcast' as const,
};

/* ── Stream URL oficial de la radio de plataforma RFW ── */
const PLATFORM_STREAM_URL = buildStreamUrl(RFW_STREAM_PARAMS);
// Resultado: https://cast3.asurahosting.com/proxy/radiofrecuenciaweb/stream

/* ── Frecuencias del dial (definición del sintonizador, no mock) ── */
const DIAL_FREQUENCIES = [
  '87.00', '87.01', '87.02', '87.03', '91.30', '94.70',
  '96.50', '97.80', '99.90', '103.50', '107.90', '110.70',
  '120.00', '130.50', '140.00', '150.30', '160.00', '170.40',
  '180.00', '190.20', '200.00',
];

/* ── Helpers compartidos ── */

function buildMockStationMap(): Map<string, RadioStation> {
  const map = new Map<string, RadioStation>();
  radioStations.forEach((s) => {
    const norm = Number.parseFloat(s.frequency).toFixed(2);
    map.set(norm, s);
  });
  return map;
}

function getProgramLabel(station: RadioStation): string {
  if (station.id === 0) return 'Contenido Exclusivo RFW';
  return `${station.category} · En Vivo`;
}

function getLocationLabel(station: RadioStation): string {
  return `${station.city}, ${station.state}`;
}

function getStatusLabel(station: RadioStation): { text: string; live: boolean } {
  if (station.id === 0) return { text: 'EN VIVO', live: true };
  if (station.isLive) return { text: 'EN VIVO', live: true };
  return { text: 'En Programación', live: false };
}

/** Mapea un RadioSearchResult de Supabase a RadioStation para el player.
 *  Para la radio de plataforma, inyecta el stream URL real de CentovaCast. */
function mapRealRadioToDisplay(radio: RadioSearchResult): RadioStation {
  const isPlatformRadio = radio.is_official;

  return {
    id: isPlatformRadio ? 0 : -1,
    name: isPlatformRadio ? 'RadioFrecuenciaWeb' : radio.name,
    slug: radio.slug,
    frequency: isPlatformRadio ? 'RFW Oficial' : (radio.frequency || ''),
    city: radio.city || '—',
    state: radio.country || '—',
    country: radio.country || '—',
    category: isPlatformRadio ? 'Oficial' : (radio.genre || 'General'),
    language: radio.language || 'es',
    hostName: isPlatformRadio ? 'RFW Oficial' : (radio.current_announcer || 'RFW'),
    listeners: radio.listener_count,
    rating: radio.rating || (isPlatformRadio ? 5.0 : 4.0),
    isLive: isPlatformRadio ? true : radio.is_live,
    logoUrl: radio.logo_url || '',
    description: isPlatformRadio
      ? 'La estación oficial de RadioFrecuenciaWeb. Contenido exclusivo 24/7.'
      : '',
    radioScore: isPlatformRadio ? 10.0 : (radio.radio_score || 0),
    cabinaMessages: 0,
    recentPosts: 0,
    isPremium: true,
    isTopRadio: isPlatformRadio,
    cabinaActiva: isPlatformRadio ? true : radio.is_live,
    hostingType: isPlatformRadio ? 'rfw_hosting' : 'external',
    streamProvider: isPlatformRadio ? 'centovacast' : 'external',
    providerType: isPlatformRadio ? 'rfw_hosted' : 'custom_url',
    streamUrl: isPlatformRadio ? PLATFORM_STREAM_URL : '',
    streamStatus: isPlatformRadio ? 'online' : (radio.is_live ? 'online' : 'offline'),
    technicalStreamStatus: isPlatformRadio ? 'active' : (radio.is_live ? 'active' : 'offline'),
    streamAvailable: isPlatformRadio ? true : radio.stream_available,
    listenerCount: radio.listener_count,
    currentMetadata: isPlatformRadio ? null : radio.current_program,
    lastTechnicalSyncAt: null,
    publicStreamUrl: isPlatformRadio ? PLATFORM_STREAM_URL : null,
    publicSecureStreamUrl: null,
  };
}

/* ── Tipo de estado del fallback ── */
type FallbackStatus = 'AVAILABLE' | 'NOT_CONFIGURED' | 'UNAVAILABLE' | 'OFFLINE' | 'DATA_SOURCE_NOT_CONFIGURED';

/* ── Now Playing metadata ── */
interface NowPlayingMeta {
  song: string | null;
  artist: string | null;
  title: string | null;
  listeners: number;
  status: 'online' | 'offline' | 'error';
  fetched_at: string;
}

/* ═══════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════ */
export default function DigitalDial() {
  const navigate = useNavigate();
  const { isPlaying: globalIsPlaying, currentStation: globalStation, play: globalPlay, pause: globalPause } = useAudioPlayer();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [animateFlip, setAnimateFlip] = useState(false);
  const knobRef = useRef<HTMLDivElement>(null);
  const startAngleRef = useRef(0);
  const accumulatedAngleRef = useRef(0);

  /* ── Mobile swipe ── */
  const swipeStartX = useRef(0);
  const swipeAccum = useRef(0);

  /* ── Estado de datos ── */
  const [occupiedSlots, setOccupiedSlots] = useState<FrequencySlot[]>([]);
  const [platformRadio, setPlatformRadio] = useState<RadioSearchResult | null>(null);
  const [fallbackStatus, setFallbackStatus] = useState<FallbackStatus>('NOT_CONFIGURED');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataSourceError, setDataSourceError] = useState(false);

  /* ── Now Playing real desde CentovaCast ── */
  const [nowPlaying, setNowPlaying] = useState<NowPlayingMeta | null>(null);

  /* ── Demo: mapa de mocks ── */
  const mockStationMap = useMemo(() => {
    if (!DEMO_ENABLED) return new Map<string, RadioStation>();
    return buildMockStationMap();
  }, []);

  /* ── Carga de datos reales desde Supabase ── */
  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        const [slots, platformStatus] = await Promise.all([
          getOccupiedFrequencies(),
          getPlatformRadioStatus(),
        ]);

        if (cancelled) return;

        setOccupiedSlots(slots);
        setDataSourceError(false);

        if (platformStatus.configured && platformStatus.radio) {
          setPlatformRadio(platformStatus.radio);

          if (platformStatus.stream_available) {
            setFallbackStatus('AVAILABLE');
          } else if (platformStatus.has_active_station) {
            setFallbackStatus('OFFLINE');
          } else {
            // La radio de plataforma puede no tener estación en Supabase
            // pero el stream de CentovaCast está disponible externamente.
            // Confiamos en que el stream hardcodeado funciona.
            setFallbackStatus('AVAILABLE');
          }
        } else {
          setFallbackStatus('NOT_CONFIGURED');
        }
      } catch {
        if (!cancelled) {
          setDataSourceError(true);
          setFallbackStatus('DATA_SOURCE_NOT_CONFIGURED');
        }
      } finally {
        if (!cancelled) setDataLoaded(true);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, []);

  /* ── Polling de Now Playing desde CentovaCast ── */
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    async function pollMetadata() {
      try {
        const meta = await fetchCentovaMetadata();
        if (!cancelled) {
          setNowPlaying(meta);
        }
      } catch {
        // Silencioso — el metadata es opcional, la música sigue sonando
      }
      if (!cancelled) {
        timer = setTimeout(pollMetadata, 15000); // cada 15 segundos
      }
    }

    pollMetadata();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const totalStations = DIAL_FREQUENCIES.length;

  /* ── Radio de fallback ── */
  const rfwRadio: RadioStation | null = useMemo(() => {
    if (platformRadio) {
      return mapRealRadioToDisplay(platformRadio);
    }
    if (DEMO_ENABLED) {
      return mockStationMap.get('87.00') || null;
    }
    return null;
  }, [platformRadio, mockStationMap]);

  /* ── Now playing label desde CentovaCast ── */
  const nowPlayingLabel = useMemo(() => {
    if (!nowPlaying || !nowPlaying.title) return null;
    let label: string;
    // Si el artista es "Unknown" no lo mostramos, solo el título
    if (nowPlaying.artist && nowPlaying.artist.trim() !== 'Unknown' && nowPlaying.artist.trim() !== '') {
      label = `${nowPlaying.artist.trim()} — ${nowPlaying.title.trim()}`;
    } else {
      label = nowPlaying.title.trim();
    }
    // Sanitizar: eliminar saltos de línea, tabs y espacios múltiples
    return label.replace(/\s+/g, ' ').trim();
  }, [nowPlaying]);

  /* ── Estación actual (DEBE ir antes de fallbackLabel e isFreeFrequency) ── */
  const currentStation: RadioStation | null = useMemo(() => {
    const freqKey = DIAL_FREQUENCIES[currentIndex];
    const freqNum = parseFloat(freqKey);

    const realSlot = occupiedSlots.find(
      (s) => Math.abs(s.frequency - freqNum) < 0.001,
    );

    if (realSlot && realSlot.radio_id) {
      if (realSlot.radio_name) {
        return {
          id: -1,
          name: realSlot.radio_name,
          slug: realSlot.radio_slug || '',
          frequency: freqKey,
          city: '—',
          state: '—',
          country: '—',
          category: 'General',
          language: 'es',
          hostName: '—',
          listeners: 0,
          rating: 4.0,
          isLive: realSlot.is_live,
          logoUrl: realSlot.logo_url || '',
          description: '',
          radioScore: 0,
          cabinaMessages: 0,
          recentPosts: 0,
          isPremium: false,
          isTopRadio: false,
          cabinaActiva: false,
          hostingType: 'external' as const,
          streamProvider: 'external' as const,
          providerType: 'custom_url' as const,
          streamUrl: '',
          streamStatus: 'online' as const,
          technicalStreamStatus: 'active' as const,
          streamAvailable: true,
          listenerCount: 0,
          currentMetadata: null,
          lastTechnicalSyncAt: null,
          publicStreamUrl: null,
          publicSecureStreamUrl: null,
        };
      }
    }

    if (DEMO_ENABLED) {
      const mockStation = mockStationMap.get(freqKey);
      if (mockStation && mockStation.id !== 0) return mockStation;
    }

    return null;
  }, [currentIndex, occupiedSlots, mockStationMap]);

  const isFreeFrequency = !currentStation;

  /* ── Etiqueta del fallback (DEBE ir después de isFreeFrequency) ── */
  const fallbackLabel = useMemo(() => {
    if (!isFreeFrequency) return null;

    switch (fallbackStatus) {
      case 'AVAILABLE':
        return {
          text: 'La frecuencia seleccionada aún no tiene una radio registrada. Mientras tanto estás escuchando RadioFrecuenciaWeb.',
          type: 'info' as const,
        };
      case 'NOT_CONFIGURED':
        return {
          text: 'Frecuencia disponible. La radio oficial de plataforma no está configurada.',
          type: 'warning' as const,
        };
      case 'UNAVAILABLE':
        return {
          text: 'Frecuencia disponible. La radio oficial no tiene stream activo en este momento.',
          type: 'warning' as const,
        };
      case 'OFFLINE':
        return {
          text: 'Frecuencia disponible. La radio oficial está temporalmente fuera del aire.',
          type: 'error' as const,
        };
      case 'DATA_SOURCE_NOT_CONFIGURED':
        return {
          text: 'No se pueden obtener datos en este momento. Verifica la conexión a Supabase.',
          type: 'error' as const,
        };
      default:
        return null;
    }
  }, [isFreeFrequency, fallbackStatus]);

  /* ── displayStation: la estación a mostrar en pantalla ── */
  const displayStation: RadioStation | null = currentStation || rfwRadio;

  /* ── Programa / categoría (sin nowPlaying para evitar duplicación visual) ── */
  const program = displayStation ? getProgramLabel(displayStation) : 'Frecuencia disponible';

  /* ── ¿Se está reproduciendo esta estación? ── */
  const isPlayingThis = !!(
    displayStation &&
    globalIsPlaying &&
    globalStation?.id === displayStation.id
  );

  /* ── Navegación ── */
  const changeStation = useCallback((newIndex: number) => {
    const clamped = Math.max(0, Math.min(totalStations - 1, newIndex));
    if (clamped !== currentIndex) {
      setAnimateFlip(true);
      setCurrentIndex(clamped);
      globalPause();
      setTimeout(() => setAnimateFlip(false), 500);
    }
  }, [currentIndex, totalStations, globalPause]);

  /* ── Play / Pause ── */
  const handlePlayToggle = () => {
    if (!displayStation) return;
    if (globalIsPlaying && globalStation?.id === displayStation.id) {
      globalPause();
    } else {
      globalPlay(displayStation);
    }
  };

  /* ── Knob drag (desktop) ── */
  const getAngle = (clientX: number, clientY: number) => {
    const knob = knobRef.current;
    if (!knob) return 0;
    const rect = knob.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    return Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    startAngleRef.current = getAngle(e.clientX, e.clientY);
    accumulatedAngleRef.current = 0;
    knobRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    const currentAngle = getAngle(e.clientX, e.clientY);
    let delta = currentAngle - startAngleRef.current;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    accumulatedAngleRef.current += delta;
    startAngleRef.current = currentAngle;
    if (Math.abs(accumulatedAngleRef.current) >= 20) {
      const direction = accumulatedAngleRef.current > 0 ? 1 : -1;
      changeStation(currentIndex + direction);
      accumulatedAngleRef.current = 0;
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
    accumulatedAngleRef.current = 0;
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const direction = e.deltaY > 0 ? 1 : -1;
    changeStation(currentIndex + direction);
  };

  useEffect(() => {
    const handleGlobalUp = () => setIsDragging(false);
    window.addEventListener('pointerup', handleGlobalUp);
    return () => window.removeEventListener('pointerup', handleGlobalUp);
  }, []);

  /* ── Mobile swipe ── */
  const handleSwipeStart = (e: React.TouchEvent) => {
    swipeStartX.current = e.touches[0].clientX;
    swipeAccum.current = 0;
  };

  const handleSwipeMove = (e: React.TouchEvent) => {
    const delta = e.touches[0].clientX - swipeStartX.current;
    swipeAccum.current = delta;
  };

  const handleSwipeEnd = () => {
    if (Math.abs(swipeAccum.current) > 50) {
      const direction = swipeAccum.current > 0 ? -1 : 1;
      changeStation(currentIndex + direction);
    }
    swipeAccum.current = 0;
  };

  /* ── Valores derivados para UI ── */
  const getKnobRotation = (index: number) => (index / totalStations) * 300 - 150;
  const knobRotation = getKnobRotation(currentIndex);
  const prevDisabled = currentIndex === 0;
  const nextDisabled = currentIndex === totalStations - 1;
  const freqBarProgress = ((currentIndex + 1) / totalStations) * 100;

  const status = displayStation ? getStatusLabel(displayStation) : { text: 'Sin señal', live: false };
  const location = displayStation ? getLocationLabel(displayStation) : '—';

  /* ── ¿Puede reproducirse? ── */
  const canPlay = !!displayStation;

  return (
    <>
      {/* ═══════════ BADGE DE DEMO ═══════════ */}
      {DEMO_ENABLED && (
        <div className="mb-3 flex justify-center">
          <span className="inline-flex items-center gap-1.5 bg-amber-500/15 text-amber-400 border border-amber-500/30 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
            Demo
          </span>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════
          MOBILE NATIVO — Experiencia App de Música
      ═══════════════════════════════════════════════════════ */}
      <div className="md:hidden relative w-full max-w-[420px] mx-auto">
        <div className="bg-foreground-900 rounded-3xl p-5 shadow-2xl">

          {/* ── PORTADA GRANDE ── */}
          <div
            className="relative mx-auto mb-4 w-[200px] h-[200px] rounded-2xl overflow-hidden bg-foreground-800 shadow-xl touch-pan-x"
            onTouchStart={handleSwipeStart}
            onTouchMove={handleSwipeMove}
            onTouchEnd={handleSwipeEnd}
          >
            {displayStation ? (
              <>
                <img
                  src={displayStation.logoUrl || ''}
                  alt={displayStation.name}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 via-transparent to-transparent pointer-events-none" />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-foreground-800">
                <div className="text-center">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-radio-line text-foreground-600 text-4xl" />
                  </div>
                  <span className="text-foreground-500 text-xs font-medium">Sin emisora</span>
                </div>
              </div>
            )}

            {/* Badge EN VIVO, RFW OFICIAL, o DEMO */}
            {isFreeFrequency && displayStation ? (
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-foreground-950/80 backdrop-blur-sm px-2.5 py-1 rounded-full border border-accent-500/30">
                <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                <span className="text-accent-400 text-[10px] font-bold">RFW OFICIAL</span>
              </div>
            ) : status.live ? (
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-foreground-950/80 backdrop-blur-sm px-2.5 py-1 rounded-full border border-accent-500/30">
                <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                <span className="text-accent-400 text-[10px] font-bold uppercase tracking-wide">{status.text}</span>
              </div>
            ) : null}

            {/* Hosting badge */}
            {displayStation && (
              <div className="absolute top-3 right-3 bg-foreground-950/80 backdrop-blur-sm px-2 py-0.5 rounded-full border border-foreground-700/50">
                <span className="text-foreground-400 text-[9px] font-medium">{hostingTypeLabels[displayStation.hostingType]}</span>
              </div>
            )}

            {/* Indicador swipe */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-foreground-950/60 backdrop-blur-sm px-3 py-1 rounded-full">
              <i className="ri-arrow-left-s-line text-background-50/50 text-xs" />
              <span className="text-background-50/60 text-[9px] font-medium">Desliza</span>
              <i className="ri-arrow-right-s-line text-background-50/50 text-xs" />
            </div>
          </div>

          {/* ── FRECUENCIA + NOMBRE ── */}
          <div className="text-center mb-1">
            <div className="flex items-baseline justify-center gap-1.5">
              <span className={`text-4xl font-extrabold tracking-tight tabular-nums leading-none transition-colors duration-300 ${animateFlip ? 'text-accent-400' : 'text-background-50'}`}>
                {DIAL_FREQUENCIES[currentIndex]}
              </span>
              <span className="text-primary-400 text-lg font-bold">RFW</span>
            </div>
            <h3 className="text-background-50 font-bold text-base mt-1.5 truncate px-4">
              {displayStation ? displayStation.name : 'Sin emisora'}
            </h3>
          </div>

          {/* ── MENSAJE DE FALLBACK ── */}
          {isFreeFrequency && fallbackLabel && (
            <div className={`mx-4 mb-2 px-3 py-2 rounded-lg text-center text-[11px] font-medium ${
              fallbackLabel.type === 'info' ? 'bg-accent-500/10 text-accent-300 border border-accent-500/20' :
              fallbackLabel.type === 'warning' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
              'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {fallbackLabel.text}
            </div>
          )}

          {/* ── INFO DEL PROGRAMA ── */}
          <div className={`transition-all duration-500 ease-out ${animateFlip ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
            <div className="flex items-center justify-center gap-2 mt-3 mb-4">
              <div className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mic-line text-accent-400 text-xs" />
                </span>
                <span className="text-accent-300 text-xs font-medium">{displayStation ? displayStation.hostName : '—'}</span>
              </div>
              <span className="text-foreground-700">·</span>
              <span className="text-foreground-400 text-xs">{program}</span>
            </div>

            {/* ── NOW PLAYING REAL ── */}
            {nowPlayingLabel && (
              <div className="flex items-center justify-center gap-1.5 mb-3">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-music-line text-accent-400 text-[10px]" />
                </span>
                <span className="text-accent-300 text-xs font-medium truncate max-w-[220px]">
                  {nowPlayingLabel}
                </span>
              </div>
            )}

            <div className="flex items-center justify-center gap-4 text-xs text-foreground-500 mb-3">
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line text-[10px]" />
                {location}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-headphone-line text-[10px]" />
                {nowPlaying?.listeners ? nowPlaying.listeners.toLocaleString() : (displayStation ? displayStation.listeners.toLocaleString() : '0')}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400 text-[10px]" />
                {displayStation ? displayStation.rating : '—'}
              </span>
            </div>
          </div>

          {/* ── BARRA DE FRECUENCIA CON SWIPE ── */}
          <div
            className="mb-4 touch-pan-x select-none"
            onTouchStart={handleSwipeStart}
            onTouchMove={handleSwipeMove}
            onTouchEnd={handleSwipeEnd}
          >
            <div className="h-2 bg-foreground-800 rounded-full overflow-hidden relative">
              <div
                className="h-full bg-accent-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${freqBarProgress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-background-50 rounded-full shadow-md border-2 border-accent-500 transition-all duration-500 ease-out"
                style={{ left: `calc(${freqBarProgress}% - 7px)` }}
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-foreground-600 text-[9px]">{DIAL_FREQUENCIES[0]}</span>
              <span className="text-foreground-500 text-[8px] uppercase tracking-wider">&larr; desliza para sintonizar &rarr;</span>
              <span className="text-foreground-600 text-[9px]">{DIAL_FREQUENCIES[totalStations - 1]}</span>
            </div>
          </div>

          {/* ── CONTROLES ── */}
          <div className="flex items-center justify-center gap-6 mb-4">
            <button
              onClick={() => changeStation(currentIndex - 1)}
              disabled={prevDisabled}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-foreground-700 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer active:scale-90"
              aria-label="Estación anterior"
            >
              <i className="ri-skip-back-fill text-background-50 text-xl" />
            </button>

            <button
              onClick={handlePlayToggle}
              disabled={!canPlay}
              className={`w-[72px] h-[72px] flex items-center justify-center rounded-full transition-all duration-300 active:scale-90 ${
                !canPlay
                  ? 'bg-foreground-800 text-foreground-600 cursor-not-allowed'
                  : isPlayingThis
                    ? 'bg-accent-500 hover:bg-accent-600 shadow-xl shadow-accent-500/40 cursor-pointer'
                    : 'bg-primary-500 hover:bg-primary-600 shadow-xl shadow-primary-500/40 cursor-pointer'
              }`}
              aria-label={isPlayingThis ? 'Pausar' : 'Reproducir'}
            >
              <i className={`text-background-50 text-3xl ${isPlayingThis ? 'ri-pause-fill' : 'ri-play-fill'}`} />
            </button>

            <button
              onClick={() => changeStation(currentIndex + 1)}
              disabled={nextDisabled}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-foreground-700 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer active:scale-90"
              aria-label="Siguiente estación"
            >
              <i className="ri-skip-forward-fill text-background-50 text-xl" />
            </button>
          </div>

          {/* ── BOTÓN ENTRAR ── */}
          <button
            onClick={() => {
              if (isFreeFrequency && platformRadio) {
                navigate(`/radio/${platformRadio.slug}`);
              } else if (currentStation) {
                navigate(`/radio/${currentStation.frequency.replace('.', '-')}`);
              } else if (rfwRadio) {
                navigate(`/radio/${rfwRadio.frequency.replace('.', '-')}`);
              }
            }}
            className={`w-full py-3.5 font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-150 text-sm whitespace-nowrap active:scale-[0.98] ${
              displayStation
                ? 'bg-background-50/10 hover:bg-background-50/20 active:bg-background-50/30 border border-background-50/20 text-background-50 cursor-pointer'
                : 'bg-foreground-800/60 border border-foreground-700/40 text-foreground-500 cursor-not-allowed'
            }`}
            disabled={!displayStation}
          >
            {isFreeFrequency && platformRadio
              ? 'Ir a RFW Radio'
              : displayStation
                ? 'Entrar a la Radio'
                : 'Sin emisora disponible'}
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          DESKTOP — Layout original preservado
      ═══════════════════════════════════════════════════════ */}
      <div className="hidden md:block relative w-full max-w-[420px] mx-auto">
        <div className="bg-foreground-900 rounded-3xl p-6 md:p-7 shadow-2xl">

          {/* ── PANTALLA PRINCIPAL ── */}
          <div className="bg-foreground-950 rounded-2xl p-5 md:p-6 mb-5 overflow-hidden">

            {/* Frecuencia grande */}
            <div className="text-center mb-1">
              <div className="text-accent-400 text-[10px] font-bold uppercase tracking-[0.35em] mb-1">
                Frecuencia
              </div>
              <div className="flex items-baseline justify-center gap-1.5">
                <span className="text-background-50 text-5xl md:text-6xl font-extrabold tracking-tight tabular-nums leading-none">
                  {DIAL_FREQUENCIES[currentIndex]}
                </span>
                <span className="text-primary-400 text-xl md:text-2xl font-bold">RFW</span>
              </div>
            </div>

            {/* Barra de frecuencia visual */}
            <div className="mt-3 mb-4">
              <div className="h-1.5 bg-foreground-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${freqBarProgress}%` }}
                />
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-foreground-600 text-[9px]">{DIAL_FREQUENCIES[0]}</span>
                <span className="text-foreground-600 text-[9px]">{DIAL_FREQUENCIES[totalStations - 1]}</span>
              </div>
            </div>

            <div className="h-px bg-foreground-800/60 my-4" />

            {/* Info de estación con animación */}
            <div
              className={`transition-all duration-500 ease-out ${animateFlip ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}
            >
              {/* ── BANNER DE FALLBACK ── */}
              {isFreeFrequency && fallbackLabel && (
                <div className={`mb-3 px-3 py-2 rounded-lg flex items-start gap-2 ${
                  fallbackLabel.type === 'info' ? 'bg-accent-500/10 border border-accent-500/20' :
                  fallbackLabel.type === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' :
                  'bg-red-500/10 border border-red-500/20'
                }`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${
                    fallbackLabel.type === 'info' ? 'bg-accent-500 animate-pulse' :
                    fallbackLabel.type === 'warning' ? 'bg-amber-500' :
                    'bg-red-500'
                  }`} />
                  <div>
                    <span className={`text-xs font-semibold ${
                      fallbackLabel.type === 'info' ? 'text-accent-400' :
                      fallbackLabel.type === 'warning' ? 'text-amber-400' :
                      'text-red-400'
                    }`}>
                      {fallbackLabel.type === 'info'
                        ? 'Frecuencia Disponible'
                        : fallbackStatus === 'DATA_SOURCE_NOT_CONFIGURED'
                          ? 'Sin Conexión'
                          : 'Frecuencia Disponible'}
                    </span>
                    <p className="text-foreground-400 text-[10px] mt-0.5 leading-relaxed">
                      {fallbackLabel.text}
                    </p>
                  </div>
                </div>
              )}

              {/* ── Estado sin displayStation ── */}
              {!displayStation && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-foreground-800/60 border border-foreground-700/40 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-foreground-500 shrink-0" />
                  <span className="text-foreground-400 text-xs font-semibold">
                    {dataSourceError ? 'DATA_SOURCE_NOT_CONFIGURED' : 'Sin emisora disponible'}
                  </span>
                  <span className="text-foreground-600 text-[10px] ml-auto">
                    {dataSourceError ? 'Verifica Supabase' : DIAL_FREQUENCIES[currentIndex]}
                  </span>
                </div>
              )}

              {/* ── Info de la estación ── */}
              {displayStation && (
                <>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl overflow-hidden bg-foreground-800 shrink-0 flex items-center justify-center border border-foreground-700/50">
                      <img
                        src={displayStation.logoUrl || ''}
                        alt={displayStation.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-background-50 font-bold text-base md:text-lg truncate leading-tight">
                        {displayStation.name}
                      </h3>

                      <div className="flex items-center gap-1.5 mt-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-mic-line text-accent-400 text-[10px]" />
                        </div>
                        <span className="text-accent-300 text-xs font-medium">{displayStation.hostName}</span>
                      </div>

                      <div className="flex items-center gap-1.5 mt-1.5">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-radio-line text-primary-400 text-[10px]" />
                        </div>
                        <span className="text-foreground-400 text-xs">{program}</span>
                      </div>

                      {/* ── NOW PLAYING REAL ── */}
                      {nowPlayingLabel && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-music-line text-accent-400 text-[10px]" />
                          </span>
                          <span className="text-accent-300 text-xs font-medium truncate">
                            {nowPlayingLabel}
                          </span>
                          {nowPlaying?.status === 'online' && (
                            <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse shrink-0" />
                          )}
                        </div>
                      )}
                    </div>

                    <div className="shrink-0 flex flex-col items-end gap-1.5">
                      {isFreeFrequency ? (
                        <div className="flex items-center gap-1.5 bg-accent-500/15 px-2.5 py-1 rounded-full border border-accent-500/20">
                          <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                          <span className="text-accent-400 text-[10px] font-bold uppercase tracking-wide">RFW OFICIAL</span>
                        </div>
                      ) : status.live ? (
                        <div className="flex items-center gap-1.5 bg-accent-500/15 px-2.5 py-1 rounded-full border border-accent-500/20">
                          <span className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-pulse" />
                          <span className="text-accent-400 text-[10px] font-bold uppercase tracking-wide">{status.text}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 bg-foreground-800/60 px-2.5 py-1 rounded-full border border-foreground-700/50">
                          <span className="w-1.5 h-1.5 bg-foreground-500 rounded-full" />
                          <span className="text-foreground-500 text-[10px] font-bold uppercase tracking-wide">{status.text}</span>
                        </div>
                      )}
                      <span className="text-[9px] text-foreground-600 font-medium px-2 py-0.5 rounded-full bg-foreground-800/50 border border-foreground-700/30">
                        {hostingTypeLabels[displayStation.hostingType]}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-foreground-800/40 grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line text-foreground-500 text-[10px]" />
                      </div>
                      <span className="text-foreground-400 text-xs">{location}</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-price-tag-3-line text-foreground-500 text-[10px]" />
                      </div>
                      <span className="text-foreground-400 text-xs">{displayStation.category}</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line text-foreground-500 text-[10px]" />
                      </div>
                      <span className="text-foreground-400 text-xs">
                        {nowPlaying?.listeners ? nowPlaying.listeners.toLocaleString() : displayStation.listeners.toLocaleString()} oyentes
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => {
                        const filled = star <= Math.round(displayStation.rating);
                        return (
                          <span key={star} className="w-3 h-3 flex items-center justify-center">
                            <i
                              className={`text-[10px] ${filled ? 'ri-star-fill text-accent-400' : 'ri-star-line text-foreground-700'}`}
                            />
                          </span>
                        );
                      })}
                      <span className="text-foreground-400 text-xs font-medium ml-0.5">{displayStation.rating}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── CONTROLES ── */}
          <div className="flex items-center justify-center gap-5">
            <button
              onClick={() => changeStation(currentIndex - 1)}
              disabled={prevDisabled}
              className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-foreground-700 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer active:scale-95"
              aria-label="Estación anterior"
            >
              <i className="ri-skip-back-line text-background-50 text-lg" />
            </button>

            <button
              onClick={handlePlayToggle}
              disabled={!canPlay}
              className={`w-16 h-16 md:w-[72px] md:h-[72px] flex items-center justify-center rounded-full transition-all duration-300 active:scale-95 ${
                !canPlay
                  ? 'bg-foreground-800 text-foreground-600 cursor-not-allowed'
                  : isPlayingThis
                    ? 'bg-accent-500 hover:bg-accent-600 shadow-xl shadow-accent-500/40 cursor-pointer'
                    : 'bg-primary-500 hover:bg-primary-600 shadow-xl shadow-primary-500/40 cursor-pointer'
              }`}
              aria-label={isPlayingThis ? 'Pausar' : 'Reproducir'}
            >
              <i className={`text-background-50 text-2xl md:text-3xl ${isPlayingThis ? 'ri-pause-fill' : 'ri-play-fill'}`} />
            </button>

            <button
              onClick={() => changeStation(currentIndex + 1)}
              disabled={nextDisabled}
              className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-foreground-800 hover:bg-foreground-700 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer active:scale-95"
              aria-label="Siguiente estación"
            >
              <i className="ri-skip-forward-line text-background-50 text-lg" />
            </button>
          </div>

          {/* ── KNOB ── */}
          <div
            ref={knobRef}
            className="mt-6 flex flex-col items-center select-none touch-none cursor-grab active:cursor-grabbing"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onWheel={handleWheel}
          >
            <p className="text-foreground-500 text-[10px] uppercase tracking-[0.2em] mb-3 font-medium">
              Gira para sintonizar
            </p>
            <div className="relative w-[88px] h-[88px] md:w-[104px] md:h-[104px]">
              <div className="absolute inset-0 rounded-full bg-foreground-800 border-2 border-foreground-700" />
              <div className="absolute inset-0">
                {Array.from({ length: 12 }).map((_, i) => {
                  const angle = (i / 12) * 360;
                  return (
                    <div
                      key={i}
                      className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-2 bg-foreground-600 origin-bottom"
                      style={{
                        height: '50%',
                        transform: `rotate(${angle}deg) translateY(-100%)`,
                        transformOrigin: 'bottom center',
                      }}
                    >
                      <div className="w-px h-2 bg-foreground-600 mx-auto" />
                    </div>
                  );
                })}
              </div>
              <div
                className="absolute inset-2 rounded-full bg-foreground-900 border border-foreground-700/50 flex items-center justify-center transition-transform duration-300 ease-out"
                style={{ transform: `rotate(${knobRotation}deg)` }}
              >
                <div className="absolute top-2 left-1/2 -translate-x-1/2">
                  <div className="w-1.5 h-4 md:h-5 bg-accent-400 rounded-full shadow-sm shadow-accent-400/50" />
                </div>
                <div className="w-3 h-3 rounded-full bg-foreground-700" />
              </div>
            </div>
          </div>

          {/* ── BOTÓN ENTRAR ── */}
          <button
            onClick={() => {
              if (isFreeFrequency && platformRadio) {
                navigate(`/radio/${platformRadio.slug}`);
              } else if (currentStation) {
                navigate(`/radio/${currentStation.frequency.replace('.', '-')}`);
              } else if (rfwRadio) {
                navigate(`/radio/${rfwRadio.frequency.replace('.', '-')}`);
              }
            }}
            className={`mt-5 w-full py-3.5 font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-200 text-sm whitespace-nowrap active:scale-[0.98] ${
              displayStation
                ? 'bg-background-50/10 hover:bg-background-50/20 border border-background-50/20 text-background-50 cursor-pointer'
                : 'bg-foreground-800/60 border border-foreground-700/40 text-foreground-500 cursor-not-allowed'
            }`}
            disabled={!displayStation}
          >
            {isFreeFrequency && platformRadio
              ? 'Ir a RFW Radio'
              : displayStation
                ? 'Entrar a la Radio'
                : 'Sin emisora disponible'}
            <i className="ri-arrow-right-line text-base" />
          </button>
        </div>
      </div>
    </>
  );
}